/**
 * Bottom tab bar for the top-level sections (Instagram-style persistence).
 *
 * This <Tabs> group is nested INSIDE the parent (app) <Stack>. Five tabs (≤5 by
 * design): Home · Queue · Calendar · Estimates · Account. Invoices + Review are
 * intentionally NOT tabs — they live on the parent stack and are reached as Home
 * hub tiles. Drilling into a detail/create route (estimates/new, estimates/[id],
 * schedule/[id], tasks/[id], invoices, review, …) pushes a full-screen screen on
 * the PARENT stack OVER the tabs — so the tab bar is hidden and a header + back
 * appears there, not here.
 *
 * Groups are URL-transparent, so resolved paths like `/(app)/estimates` are
 * unchanged by this nesting — existing router.push targets keep working.
 *
 * Headers:
 *  - Home + Account hide the tab header (Home renders its own BeamLogo; the
 *    nested account Stack owns its own headers).
 *  - Queue / Calendar / Estimates show a themed header that matches the rest of
 *    the app (reuses the brand header tokens from `stackScreenOptions`).
 *
 * Theming: active tint = brand indigo (theme.color.action.primary), inactive =
 * muted text, tab bar background = app paper, with a flat top hairline border
 * consistent with the border-only / no-shadow brand. All values come from theme
 * tokens (no raw style values — keeps style-guard happy).
 */
import { Tabs, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { stackScreenOptions } from '../_layout';
import { theme } from '../../../src/theme';
import { HelpButton } from '../../../src/components/help';
import { SCREEN_KEYS } from '../../../src/help';

// The home tab lives at `home.tsx` (→ `/home`), NOT `index.tsx`, so the public
// marketing landing can own the bare `/` route. Tell the Tabs navigator that
// `home` is its default screen.
export const unstable_settings = { initialRouteName: 'home' };

// Brand-consistent header for the tab screens that show one (Queue / Calendar /
// Estimates). Mirrors `stackScreenOptions` so they match the pushed detail
// screens on the parent stack.
const tabHeaderOptions = {
  headerStyle: stackScreenOptions.headerStyle,
  headerTintColor: stackScreenOptions.headerTintColor,
  headerTitleStyle: stackScreenOptions.headerTitleStyle,
  headerShadowVisible: stackScreenOptions.headerShadowVisible,
} as const;

export default function TabsLayout() {
  const router = useRouter();
  return (
    <Tabs
      screenOptions={{
        // Brand tints.
        tabBarActiveTintColor: theme.color.action.primary,
        tabBarInactiveTintColor: theme.color.text.secondary,
        // Paper background + flat top hairline (border-only brand, no shadow).
        tabBarStyle: {
          backgroundColor: theme.color.bg.app,
          borderTopColor: theme.color.border.default,
          borderTopWidth: theme.layout.hairline,
        },
        tabBarLabelStyle: {
          fontFamily: theme.type.small.fontFamily,
          fontSize: theme.type.small.fontSize,
        },
        // Themed header shared with the rest of the app; per-tab below decides
        // whether to show it at all.
        ...tabHeaderOptions,
        // SDK 54 / react-navigation v7: scene background moved from the
        // top-level sceneContainerStyle prop into the sceneStyle screen option.
        sceneStyle: { backgroundColor: theme.color.bg.app },
      }}
    >
      <Tabs.Screen
        name="home"
        options={{
          title: 'Home',
          headerShown: false, // Home renders its own in-screen BeamLogo.
          tabBarIcon: ({ color, size }) => <Ionicons name="home-outline" color={color} size={size} />,
          tabBarAccessibilityLabel: 'Home',
        }}
      />
      <Tabs.Screen
        name="jobs"
        options={{
          title: 'Jobs',
          headerRight: () => (
            <HelpButton compact screenKey={SCREEN_KEYS.home} onOpenHelpCenter={() => router.push('/(app)/help')} />
          ),
          tabBarIcon: ({ color, size }) => <Ionicons name="briefcase-outline" color={color} size={size} />,
          tabBarAccessibilityLabel: 'Jobs',
        }}
      />
      <Tabs.Screen
        name="estimates"
        options={{
          title: 'Estimates',
          headerRight: () => (
            <HelpButton compact screenKey={SCREEN_KEYS.estimates} onOpenHelpCenter={() => router.push('/(app)/help')} />
          ),
          tabBarIcon: ({ color, size }) => <Ionicons name="document-text-outline" color={color} size={size} />,
          tabBarAccessibilityLabel: 'Estimates',
        }}
      />
      <Tabs.Screen
        name="account"
        options={{
          title: 'Account',
          headerShown: false, // The nested account Stack owns its own headers.
          tabBarIcon: ({ color, size }) => <Ionicons name="person-circle-outline" color={color} size={size} />,
          tabBarAccessibilityLabel: 'Account',
        }}
      />
    </Tabs>
  );
}
